/**
 * Field readers and helpers for share list responses.
 *
 * The backend exposes two endpoints with related but distinct payloads:
 *   - GET /shares/received → `{ projects: [...] }` mirroring GET /projects
 *     (each entry has `id`/`project_id`, `title`, `owner_username`,
 *     `share_id`, `status`, `highlight_chapter_id`, `is_hidden`, …).
 *   - GET /shares/issued → `{ shares: [...] }` carrying share metadata
 *     only (`id`, `file_id`, `highlight_chapter_id`, `recipient_email`,
 *     `status`, `created_at`, `accepted_at`, `expires_at`, `revoked_at`).
 *     Title/recipient lookup beyond `recipient_email` is the caller's
 *     responsibility.
 *
 * All shares are book-scoped; chapter selection is expressed via the
 * optional `highlight_chapter_id` for deep-linking on first open.
 */

/**
 * Which field a recipient belongs in: anything carrying an "@" is an address,
 * everything else is a username. POST /shares takes exactly one of
 * `recipient_email` / `recipient_username` and rejects a malformed address
 * itself (422) or an unknown username (404).
 */
export const isEmailRecipient = (recipient) => (recipient || '').includes('@');

export function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.shares)) return payload.shares;
  if (payload && Array.isArray(payload.projects)) return payload.projects;
  if (payload && Array.isArray(payload.files)) return payload.files;
  if (payload && Array.isArray(payload.items)) return payload.items;
  return [];
}

export function shareTitle(share) {
  if (!share) return 'Untitled';
  return (
    share.title ||
    share.file_title ||
    share.book_title ||
    share.content_title ||
    share.name ||
    share.file?.title ||
    'Untitled'
  );
}

export function shareChapterLabel(share) {
  if (!share) return null;
  const t = share.highlight_chapter_title || share.chapter_title || share.chapter?.title || null;
  const o = share.chapter_order != null ? share.chapter_order : share.chapter?.order;
  if (t && o != null) return `Chapter ${o}: ${t}`;
  if (t) return t;
  if (o != null) return `Chapter ${o}`;
  return null;
}

export function shareRecipient(share) {
  if (!share) return 'Unknown';
  return (
    share.recipient_email ||
    share.recipient_username ||
    share.recipient?.email ||
    share.recipient?.username ||
    'Unknown'
  );
}

export function shareSharer(share) {
  if (!share) return 'Unknown';
  return (
    share.owner_username ||
    share.sharer_username ||
    share.sharer_name ||
    share.sharer?.username ||
    share.sharer?.name ||
    share.sharer?.email ||
    share.shared_by ||
    'Unknown'
  );
}

export function shareFileId(share) {
  if (!share) return null;
  return (
    share.file_id ||
    share.book_id ||
    share.content?.file_id ||
    share.file?.id ||
    share.id ||
    null
  );
}

export function shareChapterId(share) {
  if (!share) return null;
  return share.highlight_chapter_id || share.chapter_id || share.chapter?.id || null;
}

export function shareIsHidden(share) {
  if (!share) return false;
  if (typeof share.is_hidden === 'boolean') return share.is_hidden;
  if (typeof share.hidden === 'boolean') return share.hidden;
  return (share.status || '').toLowerCase() === 'hidden';
}

/**
 * Read the share-record id (as opposed to the file id) for hide/unhide/revoke
 * calls. On `/shares/received` the entry's top-level `id` is the file id and
 * the share id lives in `share_id`. On `/shares/issued` the top-level `id` is
 * the share id.
 */
export function shareRecordId(share) {
  if (!share) return null;
  return share.share_id || share.id || null;
}

export function formatShareDate(value) {
  if (value == null) return 'Unknown';
  if (typeof value === 'number') {
    return new Date(value * 1000).toLocaleDateString();
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  return d.toLocaleDateString();
}

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  pending_acceptance: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
  revoked: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  expired: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  hidden: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

export function statusBadgeClass(status) {
  const key = (status || '').toLowerCase();
  return STATUS_STYLES[key] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
}

export function statusLabel(status) {
  const s = (status || 'unknown').toString();
  return s.replace(/_/g, ' ');
}
